import React, { useState, useEffect, useRef } from 'react';
import { X, Play, CheckCircle, Terminal, Brain, Search, BookOpen, ChevronDown, ChevronRight, Zap, MessageSquare, Clock, FileText, Edit3, Copy, Check } from 'lucide-react';
import { DebugEvent, Message } from '../../hooks/useChat';
import { authFetch } from '../../api/client';
import { BUILD_NUMBER, BUILD_TIMESTAMP } from '../../version';

interface DebugPanelProps {
  events: DebugEvent[];
  onClose: () => void;
  accountId?: string;
  workspaceId?: string;
  agentSlug?: string;
  sendRaw?: (payload: object) => void;
  messages?: Message[];
}

interface ProcessedEvent {
  type: 'single' | 'thinking';
  event?: DebugEvent;
  events?: DebugEvent[];
  name?: string;
  content?: string;
  timestamp?: string;
}

function processEvents(events: DebugEvent[]): ProcessedEvent[] {
  const result: ProcessedEvent[] = [];
  let i = 0;
  while (i < events.length) {
    const ev = events[i];
    if (ev.event === 'llm_token') {
      const tokens: DebugEvent[] = [];
      const name = ev.name;
      const timestamp = ev.timestamp;
      while (i < events.length && events[i].event === 'llm_token') {
        tokens.push(events[i]);
        i++;
      }
      const content = tokens.map(t => t.content || '').join('');
      result.push({ type: 'thinking', events: tokens, name, content, timestamp });
    } else {
      result.push({ type: 'single', event: ev });
      i++;
    }
  }
  return result;
}

function getEventIcon(event: DebugEvent) {
  const name = (event.name || '').toLowerCase();
  if (event.event === 'node_start') {
    if (name === 'reasoner') return <Brain size={14} color="#a78bfa" />;
    if (name === 'tools') return <Zap size={14} color="#facc15" />;
    return <Play size={14} color="#60a5fa" />;
  }
  if (event.event === 'tool_start') {
    if (name.includes('search')) return <Search size={14} color="#f97316" />;
    if (name.includes('knowledge')) return <BookOpen size={14} color="#34d399" />;
    if (name.includes('tag')) return <Search size={14} color="#fb923c" />;
    return <Play size={14} color="#8b5cf6" />;
  }
  if (event.event === 'tool_end') return <CheckCircle size={14} color="#22c55e" />;
  return <MessageSquare size={14} color="#9ca3af" />;
}

function getEventLabel(event: DebugEvent): string {
  const name = event.name || 'unknown';
  if (event.event === 'node_start') {
    if (name === 'reasoner') return '🧠 Reasoning';
    if (name === 'tools') return '⚡ Running Tools';
    if (name === 'AgentRouter') return '🔀 Routing';
    if (name === 'SearchQueryTranslator') return '🔍 Translating Query';
    return `▶ ${name}`;
  }
  if (event.event === 'tool_start') {
    if (name === 'search_tool') return '🔍 Search Tool';
    if (name === 'knowledge_tool') return '📚 Knowledge Lookup';
    if (name === 'tag_cloud_tool') return '🏷️ Tag Cloud';
    if (name === 'qc_tool') return '✅ QC Check';
    return `🔧 ${name}`;
  }
  if (event.event === 'tool_end') {
    if (name === 'search_tool') return '✅ Search Complete';
    if (name === 'knowledge_tool') return '✅ Knowledge Retrieved';
    if (name === 'tag_cloud_tool') return '✅ Tags Retrieved';
    return `✅ ${name} Done`;
  }
  return name;
}

function formatTimestamp(ts?: string): string {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return ''; }
}

function summarizeOutput(output: any): string | null {
  if (!output) return null;
  try {
    if (typeof output === 'object' && output.content) {
      try {
        const parsed = typeof output.content === 'string' ? JSON.parse(output.content) : output.content;
        if (parsed.items) return `${parsed.items.length} results found`;
        if (parsed.total_count !== undefined) return `${parsed.total_count} total, ${(parsed.items || []).length} returned`;
        if (Array.isArray(parsed)) return `${parsed.length} items`;
      } catch { /* not JSON */ }
      if (typeof output.content === 'string') {
        if (output.content.startsWith('Knowledge Base Results:')) {
          const match = output.content.match(/\[.*\]/s);
          if (match) {
            try {
              const items = JSON.parse(match[0]);
              return `${items.length} knowledge entries`;
            } catch { /* */ }
          }
        }
        return output.content.length > 120 ? output.content.substring(0, 120) + '…' : output.content;
      }
    }
    const str = typeof output === 'string' ? output : JSON.stringify(output);
    if (str.length > 200) return str.substring(0, 200) + '…';
    return str;
  } catch { return null; }
}

const CollapsibleJson: React.FC<{ label: string; data: any; defaultOpen?: boolean }> = ({ label, data, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!data) return null;
  return (
    <div style={{ marginTop: '0.375rem' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
          fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase' as const,
          letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '0.25rem', padding: 0
        }}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {label}
      </button>
      {open && (
        <pre style={{
          fontSize: '0.6875rem', background: '#0d0d0d', padding: '0.5rem',
          borderRadius: '4px', overflowX: 'auto', marginTop: '0.25rem',
          border: '1px solid #1f1f23', color: '#d1d1d6', maxHeight: '300px',
          overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word'
        }}>
          {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
};

const ThinkingBlock: React.FC<{ content: string; name: string; timestamp?: string }> = ({ content, name, timestamp }) => {
  const [expanded, setExpanded] = useState(false);
  const preview = content.length > 80 ? content.substring(0, 80) + '…' : content;

  return (
    <div style={{
      borderLeft: '2px solid #a78bfa', paddingLeft: '0.75rem',
      paddingTop: '0.375rem', paddingBottom: '0.375rem'
    }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', color: '#e5e7eb',
          fontSize: '0.8125rem', fontWeight: 500, display: 'flex', alignItems: 'center',
          gap: '0.5rem', padding: 0, width: '100%', textAlign: 'left'
        }}
      >
        <Brain size={14} color="#a78bfa" />
        <span>💭 Internal Thinking</span>
        <span style={{ fontSize: '0.6875rem', color: '#4b5563', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Clock size={10} />
          {formatTimestamp(timestamp)}
        </span>
      </button>
      <div style={{
        fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem',
        paddingLeft: '1.375rem', fontStyle: 'italic',
        maxHeight: expanded ? 'none' : '2.5em', overflow: 'hidden',
        cursor: 'pointer'
      }} onClick={() => setExpanded(!expanded)}>
        {expanded ? content : preview}
      </div>
    </div>
  );
};

const DebugPanel: React.FC<DebugPanelProps> = ({ events, onClose, accountId, workspaceId, agentSlug, sendRaw, messages }) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [activeTab, setActiveTab] = useState<'events' | 'prompt' | 'config'>('events');
  const [systemPrompt, setSystemPrompt] = useState<string | null>(null);
  const [editedPrompt, setEditedPrompt] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptCharCount, setPromptCharCount] = useState(0);
  const [configItems, setConfigItems] = useState<any[] | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [traceCopied, setTraceCopied] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const processed = processEvents(events);

  const copyTrace = async () => {
    const trace: any = {
      timestamp: new Date().toISOString(),
      context: { accountId, workspaceId, agentSlug },
      messages: (messages || []).map(m => ({
        role: m.role,
        content: m.content?.substring(0, 2000),
        type: m.type,
        ...(m.assets ? { asset_count: m.assets.length } : {}),
      })),
      events: events.map(e => {
        const entry: any = { event: e.event, name: e.name };
        if (e.timestamp) entry.timestamp = e.timestamp;
        if (e.node) entry.node = e.node;
        if (e.input) entry.input = typeof e.input === 'string' ? e.input.substring(0, 3000) : e.input;
        if (e.output) entry.output = typeof e.output === 'string' ? e.output.substring(0, 3000) : e.output;
        if (e.content) entry.content = e.content.substring(0, 1000);
        return entry;
      }),
    };
    if (systemPrompt) {
      trace.system_prompt_chars = systemPrompt.length;
      // Include first 500 + last 500 chars to show structure without bloat
      trace.system_prompt_head = systemPrompt.substring(0, 500);
      trace.system_prompt_tail = systemPrompt.substring(Math.max(0, systemPrompt.length - 500));
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(trace, null, 2));
      setTraceCopied(true);
      setTimeout(() => setTraceCopied(false), 2000);
    } catch {
      // Fallback: open in new window
      const w = window.open('', '_blank');
      if (w) { w.document.write('<pre>' + JSON.stringify(trace, null, 2) + '</pre>'); }
    }
  };

  useEffect(() => {
    if (autoScroll && scrollRef.current && activeTab === 'events') {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events, autoScroll, activeTab]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 50);
  };

  const fetchPrompt = async () => {
    setPromptLoading(true);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8005';
    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    if (workspaceId) params.append('workspaceId', workspaceId);
    if (agentSlug) params.append('agentSlug', agentSlug);
    try {
      const resp = await authFetch(`${apiUrl}/api/debug/system-prompt?${params.toString()}`);
      if (resp.ok) {
        const data = await resp.json();
        setSystemPrompt(data.system_prompt);
        setPromptCharCount(data.char_count);
      } else {
        setSystemPrompt(`Error: ${resp.status} ${resp.statusText}`);
      }
    } catch (e: any) {
      setSystemPrompt(`Error: ${e.message}`);
    }
    setPromptLoading(false);
  };

  const fetchConfig = async () => {
    setConfigLoading(true);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8005';
    try {
      const resp = await authFetch(`${apiUrl}/api/debug/config`);
      if (resp.ok) {
        const data = await resp.json();
        setConfigItems(Array.isArray(data.config) ? data.config : []);
      } else {
        setConfigItems([]);
      }
    } catch (e: any) {
      setConfigItems([]);
    }
    setConfigLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'prompt' && !systemPrompt && !isCustom) {
      fetchPrompt();
    }
    if (activeTab === 'config' && !configItems) {
      fetchConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div className="debug-panel" style={{
      position: 'fixed', top: 0, right: 0, width: '440px', height: '100vh',
      background: '#0b0b0b', borderLeft: '1px solid #2e2e2e',
      boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.5)', zIndex: 1000,
      display: 'flex', flexDirection: 'column'
    }}>
      <div style={{
        padding: '0.75rem 1rem', borderBottom: '1px solid #2e2e2e',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#1a1a1e'
      }}>
        <h2 style={{
          fontSize: '0.9375rem', fontWeight: 600, display: 'flex',
          alignItems: 'center', gap: '0.5rem', color: '#ffffff', margin: 0
        }}>
          <Terminal size={18} /> Agent Inspector
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button onClick={copyTrace} title="Copy full trace to clipboard" style={{
            border: '1px solid #3f3f46', background: traceCopied ? '#166534' : '#27272a',
            cursor: 'pointer', color: traceCopied ? '#4ade80' : '#9ca3af',
            padding: '3px 8px', display: 'flex', alignItems: 'center', gap: '4px',
            borderRadius: '4px', fontSize: '0.6875rem', transition: 'all 0.2s'
          }}>
            {traceCopied ? <Check size={12} /> : <Copy size={12} />}
            {traceCopied ? 'Copied!' : 'Copy Trace'}
          </button>
          <span style={{ fontSize: '0.6875rem', color: '#6b7280' }}>
            {events.length} events
          </span>
          <span style={{
            fontSize: '0.625rem', color: '#4b5563', fontFamily: 'monospace',
            background: '#1e1e22', padding: '2px 6px', borderRadius: '3px'
          }}>
            b{BUILD_NUMBER} · {BUILD_TIMESTAMP}
          </span>
          <button onClick={onClose} style={{
            border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af',
            padding: '0.25rem', display: 'flex'
          }}>
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', borderBottom: '1px solid #2e2e2e', background: '#141416'
      }}>
        <button onClick={() => setActiveTab('events')} style={{
          flex: 1, padding: '0.5rem', border: 'none', cursor: 'pointer',
          background: activeTab === 'events' ? '#1a1a1e' : 'transparent',
          color: activeTab === 'events' ? '#8b5cf6' : '#6b7280',
          borderBottom: activeTab === 'events' ? '2px solid #8b5cf6' : '2px solid transparent',
          fontSize: '0.8125rem', fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem'
        }}>
          <Terminal size={14} /> Events
        </button>
        <button onClick={() => setActiveTab('prompt')} style={{
          flex: 1, padding: '0.5rem', border: 'none', cursor: 'pointer',
          background: activeTab === 'prompt' ? '#1a1a1e' : 'transparent',
          color: activeTab === 'prompt' ? '#8b5cf6' : '#6b7280',
          borderBottom: activeTab === 'prompt' ? '2px solid #8b5cf6' : '2px solid transparent',
          fontSize: '0.8125rem', fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem'
        }}>
          <FileText size={14} /> Prompt
        </button>
        <button onClick={() => setActiveTab('config')} style={{
          flex: 1, padding: '0.5rem', border: 'none', cursor: 'pointer',
          background: activeTab === 'config' ? '#1a1a1e' : 'transparent',
          color: activeTab === 'config' ? '#8b5cf6' : '#6b7280',
          borderBottom: activeTab === 'config' ? '2px solid #8b5cf6' : '2px solid transparent',
          fontSize: '0.8125rem', fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem'
        }}>
          <Zap size={14} /> Config
        </button>
      </div>

      {activeTab === 'events' ? (
      <div ref={scrollRef} onScroll={handleScroll} style={{
        flex: 1, overflowY: 'auto', padding: '0.75rem',
        scrollBehavior: 'smooth'
      }}>
        {events.length === 0 ? (
          <div style={{ color: '#4b5563', textAlign: 'center', marginTop: '3rem', fontSize: '0.875rem' }}>
            <Terminal size={32} style={{ margin: '0 auto 0.75rem', display: 'block', opacity: 0.3 }} />
            Send a message to see the agent's thinking process
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {processed.map((item, index) => {
              if (item.type === 'thinking') {
                return (
                  <ThinkingBlock key={index} content={item.content || ''} name={item.name || ''} timestamp={item.timestamp} />
                );
              }

              const ev = item.event!;
              if (ev.event === 'node_start' && ['AgentRouter', 'SearchQueryTranslator'].includes(ev.name)) {
                return null;
              }

              const summary = ev.event === 'tool_end' ? summarizeOutput(ev.output) : null;

              return (
                <div key={index} style={{
                  borderLeft: ev.event === 'tool_end' ? '2px solid #22c55e' :
                              ev.event === 'tool_start' ? '2px solid #8b5cf6' :
                              ev.event === 'node_start' ? '2px solid #3b82f6' :
                              '2px solid #374151',
                  paddingLeft: '0.75rem', paddingTop: '0.375rem', paddingBottom: '0.375rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {getEventIcon(ev)}
                    <span style={{ fontWeight: 500, color: '#e5e7eb', fontSize: '0.8125rem' }}>
                      {getEventLabel(ev)}
                    </span>
                    <span style={{ fontSize: '0.6875rem', color: '#4b5563', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Clock size={10} />
                      {formatTimestamp(ev.timestamp)}
                    </span>
                  </div>

                  {summary && (
                    <div style={{
                      fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem',
                      paddingLeft: '1.375rem'
                    }}>
                      {summary}
                    </div>
                  )}

                  <div style={{ paddingLeft: '1.375rem' }}>
                    {ev.input && <CollapsibleJson label="Input" data={ev.input} defaultOpen={ev.event === 'tool_start'} />}
                    {ev.output && <CollapsibleJson label="Output" data={ev.output} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      ) : activeTab === 'prompt' ? (
      <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem', display: 'flex', flexDirection: 'column' }}>
        {/* Status bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '0.5rem', flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.6875rem', color: '#6b7280' }}>
              {promptCharCount.toLocaleString()} chars
            </span>
            {isCustom && (
              <span style={{
                fontSize: '0.5625rem', padding: '1px 5px', borderRadius: '3px',
                background: '#5b21b6', color: '#e5e7eb',
                border: '1px solid #7c3aed',
                fontWeight: 600, textTransform: 'uppercase'
              }}>
                ✏️ custom override
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.375rem' }}>
            {!isEditing && (
              <button onClick={() => {
                setEditedPrompt(systemPrompt || '');
                setIsEditing(true);
              }} style={{
                border: '1px solid #3e3e42', background: '#1e1e22', color: '#9ca3af',
                padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer',
                fontSize: '0.6875rem', display: 'flex', alignItems: 'center', gap: '0.25rem'
              }}>
                <Edit3 size={10} /> Edit
              </button>
            )}
            {!isEditing && isCustom && (
              <button onClick={() => {
                if (sendRaw) {
                  sendRaw({ type: 'clear_custom_persona' });
                }
                setIsCustom(false);
                setSystemPrompt(null);
                fetchPrompt();
              }} style={{
                border: '1px solid #b91c1c', background: '#7f1d1d', color: '#fca5a5',
                padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer',
                fontSize: '0.6875rem', fontWeight: 600
              }}>
                Reset
              </button>
            )}
            <button onClick={() => {
              setSystemPrompt(null);
              fetchPrompt();
            }} style={{
              border: '1px solid #3e3e42', background: '#1e1e22', color: '#9ca3af',
              padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer',
              fontSize: '0.6875rem'
            }}>
              Refresh
            </button>
          </div>
        </div>

        {/* Content area */}
        {promptLoading ? (
          <div style={{ color: '#6b7280', textAlign: 'center', marginTop: '2rem' }}>Loading system prompt...</div>
        ) : isEditing ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <textarea
              value={editedPrompt}
              onChange={(e) => setEditedPrompt(e.target.value)}
              style={{
                flex: 1, width: '100%', minHeight: '300px',
                background: '#141416', color: '#d1d5db', border: '1px solid #8b5cf6',
                borderRadius: '6px', padding: '0.75rem', fontSize: '0.75rem',
                fontFamily: 'monospace', lineHeight: 1.5, resize: 'vertical',
                outline: 'none'
              }}
            />
            <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setIsEditing(false)} style={{
                border: '1px solid #3e3e42', background: '#1e1e22', color: '#9ca3af',
                padding: '0.375rem 0.75rem', borderRadius: '4px', cursor: 'pointer',
                fontSize: '0.6875rem'
              }}>
                Cancel
              </button>
              <button onClick={() => {
                if (sendRaw) {
                  sendRaw({ type: 'set_custom_persona', custom_persona: editedPrompt });
                }
                setIsCustom(true);
                setSystemPrompt(editedPrompt);
                setPromptCharCount(editedPrompt.length);
                setIsEditing(false);
              }} style={{
                border: '1px solid #7c3aed', background: '#5b21b6', color: '#e5e7eb',
                padding: '0.375rem 0.75rem', borderRadius: '4px', cursor: 'pointer',
                fontSize: '0.6875rem', fontWeight: 600
              }}>
                Apply Custom Prompt
              </button>
            </div>
          </div>
        ) : systemPrompt ? (
          <pre style={{
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontSize: '0.75rem', color: '#d1d5db', lineHeight: 1.5,
            background: '#141416', padding: '0.75rem', borderRadius: '6px',
            border: '1px solid #2e2e2e', margin: 0, flex: 1
          }}>
            {systemPrompt}
          </pre>
        ) : (
          <div style={{ color: '#4b5563', textAlign: 'center', marginTop: '3rem', fontSize: '0.875rem' }}>
            <FileText size={32} style={{ margin: '0 auto 0.75rem', display: 'block', opacity: 0.3 }} />
            Select an agent to view the system prompt
          </div>
        )}
      </div>
      ) : activeTab === 'config' ? (
      <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem' }}>
        {configLoading ? (
          <div style={{ color: '#6b7280', textAlign: 'center', marginTop: '2rem' }}>Loading config...</div>
        ) : configItems ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
              <button onClick={() => { setConfigItems(null); fetchConfig(); }} style={{
                border: '1px solid #3e3e42', background: '#1e1e22', color: '#9ca3af',
                padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer',
                fontSize: '0.6875rem'
              }}>
                Refresh
              </button>
            </div>
            {configItems.map((item: any) => (
              <div key={item.name} style={{
                padding: '0.5rem 0.625rem', marginBottom: '0.375rem',
                background: '#141416', borderRadius: '6px',
                border: '1px solid #2e2e2e', fontSize: '0.75rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ color: '#e5e7eb', fontFamily: 'monospace', fontWeight: 500 }}>{item.name}</span>
                  <span style={{
                    fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase',
                    padding: '1px 6px', borderRadius: '3px',
                    background: item.source === 'env' ? '#065f46' : '#1e1e22',
                    color: item.source === 'env' ? '#6ee7b7' : '#6b7280',
                    border: `1px solid ${item.source === 'env' ? '#047857' : '#3e3e42'}`
                  }}>
                    {item.source}
                  </span>
                </div>
                <div style={{ color: '#9ca3af', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {item.value || <span style={{ color: '#4b5563', fontStyle: 'italic' }}>not set</span>}
                </div>
                {item.source === 'default' && item.default && (
                  <div style={{ color: '#4b5563', fontSize: '0.6875rem', marginTop: '0.125rem' }}>
                    default: {item.default}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#4b5563', textAlign: 'center', marginTop: '3rem' }}>No config data</div>
        )}
      </div>
      ) : null}
    </div>
  );
};

export default DebugPanel;
